import services from "../../api/service";
import blogs from "../../api/blogs";
import projects from "../../api/project";
import faqItems from "../../api/faq";

// Shared by the SEO component (in the browser) and scripts/prerender.js (at build time),
// so crawlers that do not run JavaScript receive the same tags as users.

export const SITE_URL = "https://www.laskontech.com";
export const SITE_NAME = "Laskon Technologies";
export const LEGAL_NAME = "Laskon Technologies Ltd";
export const DEFAULT_IMAGE = `${SITE_URL}/Lason_Logo.jpg`;
export const CONTACT = {
  email: "sales@laskontech.com",
  telephone: "+44 7466 436417",
  streetAddress: "Unit 1, Parliament Business Centre, Commerce Way",
  addressLocality: "Liverpool",
  postalCode: "L8 7BL",
  addressCountry: "GB",
};
export const SOCIAL_PROFILES = ["https://www.linkedin.com/company/laskon-technologies/"];

const ORGANIZATION_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

export const pageMetadata = {
  "/": {
    title: "Software Development, AI & Digital Solutions | Laskon Technologies",
    heading: "Software Development, AI & Digital Solutions",
    description:
      "Laskon Technologies builds custom software, AI solutions, cloud platforms and digital products. Explore our services and discuss your next project with our team.",
    keywords:
      "custom software development, software development company, AI solutions, web app development, cloud solutions, cybersecurity services, digital transformation",
  },
  "/about": {
    title: "About Laskon Technologies | Software & Digital Solutions",
    heading: "About Laskon Technologies",
    description:
      "Meet Laskon Technologies, a technology partner delivering software engineering, AI, cloud, cybersecurity and digital transformation services.",
    keywords: "about Laskon Technologies, technology partner, software engineering team, digital transformation company",
  },
  "/service": {
    title: "Technology Services | Software, AI, Cloud & Cybersecurity",
    heading: "Technology Services",
    description:
      "Explore Laskon Technologies services, including custom software development, AI and machine learning, data engineering, cloud, cybersecurity, QA and digital marketing.",
    keywords: "software development services, AI consulting, data engineering, cloud migration, cybersecurity, QA testing, digital marketing",
  },
  "/portfolio": {
    title: "Technology Portfolio & Case Studies | Laskon Technologies",
    heading: "Portfolio & Case Studies",
    description:
      "Explore digital products and technology projects delivered by Laskon Technologies across software, mobile applications and business platforms.",
    keywords: "software development portfolio, technology case studies, mobile app projects, digital product development",
  },
  "/blog": {
    title: "Technology Insights & Guides | Laskon Technologies",
    heading: "Technology Insights & Guides",
    description:
      "Read practical insights from Laskon Technologies on software engineering, AI, cloud, cybersecurity and digital product development.",
    keywords: "software development blog, AI technology insights, cloud computing guides, cybersecurity articles",
  },
  "/contact": {
    title: "Contact Laskon Technologies | Discuss Your Project",
    heading: "Contact Laskon Technologies",
    description:
      "Contact Laskon Technologies to discuss custom software, AI, cloud, cybersecurity or digital transformation for your business.",
    keywords: "contact Laskon Technologies, software development consultation, discuss a technology project",
  },
  "/career": {
    title: "Careers | Laskon Technologies",
    heading: "Careers",
    description: "Explore career opportunities and connect with the team at Laskon Technologies.",
    keywords: "Laskon Technologies careers, technology jobs, software engineering careers",
  },
  "/pricing": {
    title: "Technology Services & Engagement | Laskon Technologies",
    heading: "Technology Services & Engagement",
    description: "Talk with Laskon Technologies about the right engagement for your software and digital transformation project.",
    keywords: "software development project pricing, technology consulting engagement, custom software services",
  },
  "/team": {
    title: "Our Team | Laskon Technologies",
    heading: "Our Team",
    description: "Meet the people behind Laskon Technologies’ software, AI and digital solutions.",
    keywords: "Laskon Technologies team, software development experts, technology consultants",
  },
  "/PrivacyPolicy": {
    title: "Privacy Policy | Laskon Technologies",
    heading: "Privacy Policy",
    description: "Read the privacy policy for the Laskon Technologies website and services.",
    keywords: "Laskon Technologies privacy policy",
  },
};

const fallbackMetadata = {
  title: "Technology Solutions | Laskon Technologies",
  heading: "Technology Solutions",
  description: "Discover software, AI and digital solutions from Laskon Technologies.",
  keywords: "Laskon Technologies, software development, AI solutions",
};

// Breadcrumb parent for each detail route
const sections = {
  service: { name: "Services", path: "/service" },
  blog: { name: "Blog", path: "/blog" },
  project: { name: "Portfolio", path: "/portfolio" },
};

export const summarize = (value = "") => {
  const text = value.replace(/\s+/g, " ").trim();
  if (text.length <= 155) return text;
  return `${text.slice(0, 152).replace(/\s+\S*$/, "")}...`;
};

export const toAbsoluteUrl = (path) => (/^https?:\/\//.test(path) ? path : `${SITE_URL}${path}`);

// Bundled images resolve to "/static/media/..."; tiny images are inlined as data URIs and unusable for previews
const imageUrl = (src) => (typeof src === "string" && src.startsWith("/") ? toAbsoluteUrl(src) : DEFAULT_IMAGE);

// Blog dates are stored as "DD/MM/YYYY", sometimes with spaces around the slashes
export const toIsoDate = (value = "") => {
  const match = value.replace(/\s/g, "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return undefined;
  const [, day, month, year] = match;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

export const normalizePath = (pathname = "/") => (pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname);

export const findPageEntity = (pathname) => {
  const [, section, slug] = normalizePath(pathname).match(/^\/(service-single|blog-single|portfolio_details)\/([^/]+)$/) || [];
  if (section === "service-single") {
    const item = services.find((service) => service.slug === slug);
    return item && { kind: "service", item };
  }
  if (section === "blog-single") {
    const item = blogs.find((blog) => blog.slug === slug);
    return item && { kind: "blog", item };
  }
  if (section === "portfolio_details") {
    const item = projects.find((project) => project.slug === slug);
    return item && { kind: "project", item };
  }
  return undefined;
};

const entityMetadata = ({ kind, item }) => {
  if (kind === "blog") {
    return {
      title: `${item.title} | Laskon Technologies`,
      heading: item.title,
      description: summarize(item.summary || item.description),
      keywords: `${item.title.toLowerCase()}, software development insights, Laskon Technologies`,
      image: imageUrl(item.screens),
      ogType: "article",
      published: toIsoDate(item.create_at),
    };
  }
  if (kind === "project") {
    return {
      title: `${item.title} | Laskon Technologies Portfolio`,
      heading: item.title,
      description: summarize(item.description),
      keywords: `${item.title.toLowerCase()}, software development case study, Laskon Technologies`,
      image: imageUrl(item.pImg),
    };
  }
  return {
    title: `${item.title} | Laskon Technologies`,
    heading: item.title,
    description: summarize(item.description),
    keywords: `${item.title.toLowerCase()}, ${item.features.slice(0, 3).join(", ").toLowerCase()}, Laskon Technologies`,
  };
};

export const organizationSchema = {
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: LEGAL_NAME,
  alternateName: SITE_NAME,
  url: SITE_URL,
  logo: { "@type": "ImageObject", url: DEFAULT_IMAGE, width: 1095, height: 732 },
  image: DEFAULT_IMAGE,
  email: CONTACT.email,
  telephone: CONTACT.telephone,
  address: {
    "@type": "PostalAddress",
    streetAddress: CONTACT.streetAddress,
    addressLocality: CONTACT.addressLocality,
    postalCode: CONTACT.postalCode,
    addressCountry: CONTACT.addressCountry,
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: CONTACT.email,
    telephone: CONTACT.telephone,
    availableLanguage: "English",
  },
  sameAs: SOCIAL_PROFILES,
  knowsAbout: services.map((service) => service.title),
};

const breadcrumbSchema = (crumbs) => ({
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: toAbsoluteUrl(crumb.path),
  })),
});

export const getPageSeo = (pathname) => {
  const path = normalizePath(pathname);
  const entity = findPageEntity(path);
  const metadata = {
    ogType: "website",
    image: DEFAULT_IMAGE,
    ...(entity ? entityMetadata(entity) : pageMetadata[path] || fallbackMetadata),
  };
  const canonicalUrl = toAbsoluteUrl(path === "/" ? "/" : path);
  const organizationRef = { "@id": ORGANIZATION_ID };

  const graph = [
    organizationSchema,
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      name: SITE_NAME,
      url: SITE_URL,
      inLanguage: "en-GB",
      publisher: organizationRef,
    },
    {
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      name: metadata.title,
      description: metadata.description,
      url: canonicalUrl,
      inLanguage: "en-GB",
      isPartOf: { "@id": WEBSITE_ID },
      about: organizationRef,
      primaryImageOfPage: metadata.image,
    },
  ];

  if (path !== "/") {
    const crumbs = [{ name: "Home", path: "/" }];
    if (entity) crumbs.push(sections[entity.kind]);
    crumbs.push({ name: metadata.heading, path });
    graph.push(breadcrumbSchema(crumbs));
  }

  if (entity?.kind === "service") {
    graph.push({
      "@type": "Service",
      name: entity.item.title,
      description: entity.item.description,
      provider: organizationRef,
      url: canonicalUrl,
      serviceType: entity.item.thumb1 || entity.item.title,
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: entity.item.title,
        itemListElement: entity.item.features.map((feature) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: feature },
        })),
      },
    });
  }
  if (entity?.kind === "blog") {
    graph.push({
      "@type": "BlogPosting",
      headline: entity.item.title,
      description: metadata.description,
      image: metadata.image,
      datePublished: metadata.published,
      dateModified: metadata.published,
      author: organizationRef,
      publisher: organizationRef,
      mainEntityOfPage: canonicalUrl,
      inLanguage: "en-GB",
    });
  }
  if (entity?.kind === "project") {
    graph.push({
      "@type": "CreativeWork",
      name: entity.item.title,
      description: metadata.description,
      image: metadata.image,
      genre: entity.item.sub,
      keywords: entity.item.features?.join(", "),
      creator: organizationRef,
      url: canonicalUrl,
    });
  }
  if (path === "/") {
    graph.push({
      "@type": "FAQPage",
      mainEntity: faqItems.map(({ question, answer }) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer },
      })),
    });
  }

  return {
    ...metadata,
    canonicalUrl,
    robots: "index, follow, max-image-preview:large",
    entity,
    structuredData: { "@context": "https://schema.org", "@graph": graph },
  };
};

// Pages listed in the sitemap and pre-rendered as static HTML
export const indexablePaths = [
  "/",
  "/about",
  "/service",
  "/portfolio",
  "/blog",
  "/contact",
  "/career",
  "/PrivacyPolicy",
  ...services.map((service) => `/service-single/${service.slug}`),
  ...projects.map((project) => `/portfolio_details/${project.slug}`),
  ...blogs.map((blog) => `/blog-single/${blog.slug}`),
];

export { services, blogs, projects, faqItems };
